import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { fetchUsersByOrganization } from '../../../services/Http/Api';
import { useNavigate } from 'react-router-dom';
import UserCreate from './components/UserCreate';
import { useTranslation } from 'react-i18next';
import { MdAdd, MdEdit, MdAccountCircle, MdReceipt } from 'react-icons/md';
import UserModal from './components/UserModal';
import SectionTitle from '../../../shared/layout/titles/SectionTitle';
import MainButton from '../../../shared/layout/buttons/MainButton';
import IconButton from '../../../shared/layout/buttons/IconButton';
import TableCellWithIcon from '../../../shared/layout/tables/TableCellWithIcon';
import UsersListSkeleton from '../../common/skeletons/UsersListSkeleton';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import { MdSearch } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useCurrency } from '../../../context/CurrencyProvider';

const UserAvatar = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== '$own',
})`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${props => props.$own ? 'rgba(63, 195, 128, 0.1)' : 'rgba(130, 22, 146, 0.1)'};
    color: ${props => props.$own ? '#3fc380' : 'var(--color-primary)'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    margin-right: 0.75rem;
    border: 1px solid ${props => props.$own ? 'rgba(63, 195, 128, 0.2)' : 'rgba(130, 22, 146, 0.2)'};
`;

const StatusBadge = styled.span.withConfig({
    shouldForwardProp: (prop) => prop !== '$own',
})`
    padding: 0.375rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    background: ${props => props.$own ? 'rgba(63, 195, 128, 0.15)' : 'rgba(130, 22, 146, 0.1)'};
    color: ${props => props.$own ? '#27ae60' : 'var(--color-primary)'};
    border: 1px solid ${props => props.$own ? 'rgba(63, 195, 128, 0.3)' : 'rgba(130, 22, 146, 0.2)'};
    display: inline-block;
`;

const SearchContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    background: #f3f4f6;
    border-radius: 8px;
    padding: 0 0.75rem;
    margin-right: 1rem;
    flex: 1;
    max-width: 300px;
    border: 1px solid transparent;
    transition: all 0.2s;

    &:focus-within {
        background: #fff;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 2px rgba(130, 22, 146, 0.1);
    }

    input {
        background: transparent;
        border: none;
        padding: 0.625rem 0.5rem;
        width: 100%;
        outline: none;
        font-size: 0.875rem;
        color: #111827;
    }

    svg {
        color: #6b7280;
    }
`;

const ActionWrapper = styled.div`
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    width: 100%;
`;


const UsersList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { formatResolvedAmount } = useCurrency();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUserModal, setShowUserModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('id');
    const [sortDirection, setSortDirection] = useState('asc');
    const [searchTerm, setSearchTerm] = useState('');


    const loadUsers = async (page) => {
        setLoading(true);
        try {
            const { users, total, currentPage } = await fetchUsersByOrganization(page, perPage, sortBy, sortDirection);
            setUsers(users);
            setTotalRows(total);
            setCurrentPage(currentPage);
        } catch (error) {
            console.error('Error fetching customers:', error);
            toast.error(t('Users.UsersList.loadError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers(currentPage);
    }, [currentPage, perPage, sortBy, sortDirection]);

    const handleAddCustomer = () => {
        setModalMode('create');
        setSelectedCustomerId(null);
        setShowUserModal(true);
    };

    const handleEditCustomer = (customerId) => {
        setModalMode('edit');
        setSelectedCustomerId(customerId);
        setShowUserModal(true);
    };

    const handleOpenDetails = (row) => {
        const targetId = row?.user_id || row?.id;
        if (!targetId) return;
        navigate(`/customers/${targetId}`);
    };

    const handleUserModalClose = () => {
        setShowUserModal(false);
        setSelectedCustomerId(null);
        setModalMode('create');
    };

    const handleUserAdded = () => {
        loadUsers(currentPage);
        handleUserModalClose();
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        loadUsers(page);
    };

    const handleSort = (column, sortDirection) => {
        setSortBy(column.key);
        setSortDirection(sortDirection);
    };

    const columns = [
        {
            name: t('Users.UsersList.columns.userId'),
            key: 'user_id',
            selector: row => row.user_id,
            sortable: true
        },
        {
            name: t('Users.UsersList.columns.name'),
            key: 'user_name',
            selector: row => row.user_name,
            sortable: true,
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <UserAvatar $own={row.own_buyer}>
                        {row.user_name?.charAt(0) || <MdAccountCircle />}
                    </UserAvatar>
                    <div style={{ fontWeight: 500, color: '#111827' }}>{row.user_name}</div>
                </div>
            )
        },
        {
            name: t('Users.UsersList.columns.status'),
            cell: row => (
                <StatusBadge $own={row.own_buyer}>
                    {row.own_buyer ? t('Users.UsersList.ownUser') : t('Users.UsersList.globalUser')}
                </StatusBadge>
            ),
            sortable: true,
            selector: row => row.own_buyer,
        },
        {
            name: t('Users.UsersList.columns.totalSpent'),
            key: 'total_spent',
            selector: row => parseFloat(row.total_spent),
            sortable: true,
            cell: row => (
                <div style={{ fontWeight: 600, color: '#111827' }}>
                    {formatResolvedAmount({
                        eurAmount: row.total_spent,
                        localAmount: row.total_spent_local,
                        localCurrencyId: row.currency_id,
                    })}
                </div>
            )
        },

        {
            name: t('Users.UsersList.columns.firstTransactionDate'),
            key: 'first_transaction_date',
            selector: row => new Date(row.first_transaction_date),
            sortable: true,
            cell: row => (
                <TableCellWithIcon>
                    <MdReceipt size={20} color="rgba(130, 22, 146, 0.8)" />
                    {row.first_transaction_date ? new Date(row.first_transaction_date).toLocaleDateString() : "N/A"}
                </TableCellWithIcon>
            )
        },
        {
            name: t('Users.UsersList.columns.lastTransactionDate'),
            key: 'last_transaction_date',
            selector: row => new Date(row.last_transaction_date),
            sortable: true,
            cell: row => (
                <TableCellWithIcon>
                    <MdReceipt size={20} color="rgba(130, 22, 146, 0.8)" />
                    {row.last_transaction_date ? new Date(row.last_transaction_date).toLocaleDateString() : "N/A"}
                </TableCellWithIcon>
            )
        },
        {
            name: t('Users.UsersList.columns.countOfItems'),
            key: 'count_of_items',
            selector: row => row.count_of_items,
            sortable: true,
            cell: row => (
                <div style={{ textAlign: 'right', width: '100%' }}>
                    {row.count_of_items}
                </div>
            )
        },
        {
            name: t('Users.UsersList.columns.venuesCount'),
            key: 'venues_count',
            selector: row => row.venues_statistics?.length,
            sortable: false,
            cell: row => (
                <div style={{ textAlign: 'right', width: '100%' }}>
                    {row.venues_statistics?.length || 0}
                </div>
            )
        },

        {
            name: '',
            cell: row => (
                <ActionWrapper>
                    {row.own_buyer && (
                        <IconButton noBg onClick={() => handleEditCustomer(row.user_id || row.id)}>
                            <MdEdit style={{ color: '#27ae60' }} />
                        </IconButton>
                    )}
                </ActionWrapper>
            ),
            ignoreRowClick: true,
            width: '100px',
        },
    ];




    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <SectionTitle>{t('Users.UsersList.title')}</SectionTitle>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <SearchContainer>
                        <MdSearch size={20} />
                        <input
                            type="text"
                            placeholder={t('Users.UsersList.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </SearchContainer>
                    <MainButton size="small" onClick={handleAddCustomer}>
                        <MdAdd size={20} />
                        {t('Users.UsersList.addUserButton')}
                    </MainButton>
                </div>
            </div>

            <StyledCard style={{ padding: 0 }}>
                {loading ? (
                    <UsersListSkeleton />
                ) : (
                    <DataTable
                        columns={columns}
                        data={users.filter(u => u.user_name?.toLowerCase().includes(searchTerm.toLowerCase()))}
                        onRowClicked={handleOpenDetails}
                        pointerOnHover
                        pagination={totalRows > perPage}
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        highlightOnHover
                        onSort={handleSort}
                        sortServer
                        noDataComponent={
                            <div style={{ padding: '1.5rem', color: '#6b7280' }}>
                                {t('Users.UsersList.noCustomers')}
                            </div>
                        }
                        customStyles={{
                            table: {
                                style: {
                                    backgroundColor: 'transparent',
                                },
                            },
                            headRow: {
                                style: {
                                    backgroundColor: '#f9fafb',
                                    minHeight: '3.25rem',
                                    borderBottom: '1px solid #e5e7eb',
                                },
                            },
                            headCells: {
                                style: {
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                },
                            },
                            rows: {
                                style: {
                                    minHeight: '4.5rem',
                                    '&:not(:last-of-type)': {
                                        borderBottom: '1px solid #f3f4f6',
                                    },
                                    '&:hover': {
                                        backgroundColor: '#f9fafb !important',
                                        transition: 'bg 0.2s',
                                    },
                                },
                            },
                        }}
                    />
                )}
            </StyledCard>
            <UserModal
                show={showUserModal}
                onClose={handleUserModalClose}
                onUserAdded={handleUserAdded}
                mode={modalMode}
                customerId={selectedCustomerId}
            />
        </div>
    );
};

export default UsersList;
